/* eslint-disable react/no-danger */
/* eslint-disable prefer-destructuring */
import { useState } from "react";
import { Chip, Collapse, Divider, Icon, IconButton, Tooltip } from "@mui/material";
import lod_ from "lodash";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";

/**
 * Filters out empty or nil emails from an array or a single value.
 */
const getValidEmails = (emails = []) => {
	if (lod_.isNil(emails) || lod_.isEmpty(emails)) return [];
	const list = Array.isArray(emails) ? emails : [emails];
	return list.filter(e => !lod_.isNil(e) && !lod_.isEmpty(e));
};

/**
 * Displays a single message in a conversation.
 * @param {Object} message - The message object containing header and payload.
 * @returns {JSX.Element} The rendered component.
 */
const Message = ({ message }) => {
	const [showDetails, setShowDetails] = useState(false);
	const [showHtml, setShowHtml] = useState(true);

	let styleDirection = {};

	let header = message.header;

	if (header.source === "contact") {
		styleDirection = {
			left: 10,
			maxWidth: "80%",
			marginRight: "20%",
			backgroundColor: "white",
			borderRadius: "15px 15px 15px 0",
			padding: 10
		};
	} else if (["cockpit", "bot"].includes(header.source)) {
		styleDirection = {
			right: 10,
			maxWidth: "80%",
			marginLeft: "20%",
			backgroundColor: "#2196f3",
			color: "white",
			fontWeight: "bold",
			borderRadius: "15px 15px 0 15px",
			padding: 10
		};
	}

	const from = message?.header?.from?.id ?? "";
	const to = message?.header?.to?.id ?? message?.header?.to?.to ?? "";
	const ccEmails = getValidEmails(message?.header?.cc);
	const bccEmails = getValidEmails(message?.header?.bcc);
	const subject = message?.payload?.payload?.subject;
	const htmlText = message?.payload?.payload?.text?.html_text;
	const answer =
		message?.payload?.payload?.text?.parts?.answer ?? message?.payload?.payload?.text?.plain_text;

	const hasExtraRecipients = ccEmails.length > 0 || bccEmails.length > 0;
	const hasBothFormats = !!htmlText && !!answer;

	const isContact = header.source === "contact";
	const labelColor = isContact ? "text.secondary" : "rgba(255,255,255,0.7)";
	const valueColor = isContact ? "text.primary" : "rgba(255, 255, 255, 0.7)";

	return (
		<MDBox
			mt={1}
			style={{ ...styleDirection, position: "relative", overflow: "hidden", minWidth: 0 }}
		>
			{/* Toggle HTML / Texte */}
			{hasBothFormats && (
				<Tooltip
					title={
						showHtml
							? t("FORMS.RESOURCE.listConversations.showClean")
							: t("FORMS.RESOURCE.listConversations.showHTML")
					}
					placement="top"
				>
					<IconButton
						size="small"
						onClick={() => setShowHtml(!showHtml)}
						sx={{
							position: "absolute",
							top: 4,
							right: 4,
							p: 0.5,
							color: labelColor
						}}
						title={
							showHtml
								? t("FORMS.RESOURCE.listConversations.showClean")
								: t("FORMS.RESOURCE.listConversations.showHTML")
						}
					>
						<Icon fontSize="small">{showHtml ? "code" : "article"}</Icon>
					</IconButton>
				</Tooltip>
			)}

			{/* Header: From / To / Subject */}
			<MDBox mb={1}>
				{/* From */}
				<MDBox display="flex" alignItems="center" mb={0.3}>
					<MDTypography variant="caption" fontWeight="bold" sx={{ color: labelColor, mr: 0.5 }}>
						{t("FORMS.RESOURCE.listConversations.messageFrom")} :
					</MDTypography>
					<MDTypography variant="caption" sx={{ color: valueColor }}>
						{from}
					</MDTypography>
				</MDBox>

				{/* To */}
				<MDBox
					display="flex"
					alignItems="flex-start"
					mb={0.3}
					sx={{ overflow: "hidden", minWidth: 0 }}
				>
					<MDTypography variant="caption" fontWeight="bold" sx={{ color: labelColor, mr: 0.5 }}>
						{t("FORMS.RESOURCE.listConversations.messageTarget")} :
					</MDTypography>
					<MDTypography
						variant="caption"
						sx={{
							color: valueColor,
							wordBreak: "break-word",
							overflowWrap: "anywhere",
							minWidth: 0
						}}
					>
						{Array.isArray(to)
							? to.map((e, i) => {
									return i === to.length - 1 ? `${e}` : `${e}, `;
								})
							: `${to}`}
					</MDTypography>

					{hasExtraRecipients && (
						<IconButton
							size="small"
							onClick={() => setShowDetails(!showDetails)}
							sx={{ ml: 0.5, p: 0.3, width: 20, height: 20, color: valueColor }}
						>
							<Icon fontSize="small">{showDetails ? "expand_less" : "expand_more"}</Icon>
						</IconButton>
					)}
				</MDBox>

				{/* CC / BCC collapsible */}
				<Collapse in={showDetails}>
					{ccEmails.length > 0 && (
						<MDBox display="flex" alignItems="start" mb={0.3}>
							<MDTypography
								variant="caption"
								fontWeight="bold"
								sx={{ color: labelColor, mr: 0.5, mt: 0.3 }}
							>
								Cc :
							</MDTypography>
							<MDBox display="flex" flexWrap="wrap" flex={1}>
								{ccEmails.map((email, i) => (
									<Chip
										key={i}
										label={email}
										size="small"
										sx={{
											mr: 0.5,
											mb: 0.3,
											fontSize: "0.7rem",
											height: 20,
											backgroundColor: isContact ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.2)",
											color: valueColor,
											"& .MuiChip-label": { px: 0.8 }
										}}
									/>
								))}
							</MDBox>
						</MDBox>
					)}
					{bccEmails.length > 0 && (
						<MDBox display="flex" alignItems="start" mb={0.3}>
							<MDTypography
								variant="caption"
								fontWeight="bold"
								sx={{ color: labelColor, mr: 0.5, mt: 0.3 }}
							>
								Bcc :
							</MDTypography>
							<MDBox display="flex" flexWrap="wrap" flex={1}>
								{bccEmails.map((email, i) => (
									<Chip
										key={i}
										label={email}
										size="small"
										sx={{
											mr: 0.5,
											mb: 0.3,
											fontSize: "0.7rem",
											height: 20,
											backgroundColor: isContact ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.2)",
											color: valueColor,
											"& .MuiChip-label": { px: 0.8 }
										}}
									/>
								))}
							</MDBox>
						</MDBox>
					)}
				</Collapse>

				{/* Subject */}
				{subject && (
					<MDBox
						display="flex"
						alignItems="flex-start"
						mt={0.3}
						sx={{ overflow: "hidden", minWidth: 0 }}
					>
						<MDTypography
							variant="caption"
							fontWeight="bold"
							sx={{ color: labelColor, mr: 0.5, flexShrink: 0 }}
						>
							{t("FORMS.RESOURCE.listConversations.messageSubject")} :
						</MDTypography>
						<MDTypography
							variant="caption"
							fontWeight="medium"
							sx={{
								color: valueColor,
								wordBreak: "break-word",
								overflowWrap: "anywhere",
								minWidth: 0
							}}
						>
							{subject}
						</MDTypography>
					</MDBox>
				)}
			</MDBox>

			<Divider sx={{ borderColor: isContact ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.3)" }} />

			{/* Content */}
			{showHtml && htmlText ? (
				<MDBox
					sx={{
						fontSize: 12,
						color: labelColor,
						wordBreak: "break-word",
						overflowWrap: "anywhere",
						overflow: "hidden",
						minWidth: 0,
						"& a": { color: isContact ? "#1A73E8" : "#bbdefb", wordBreak: "break-all" },
						"& img": { maxWidth: "100%", height: "auto", display: "block" },
						"& pre": { whiteSpace: "pre-wrap !important", wordBreak: "break-word !important" },
						"& table": { maxWidth: "100%", tableLayout: "fixed" },
						"& td, & th": { wordBreak: "break-word", overflowWrap: "anywhere" },
						"& *": {
							maxWidth: "100% !important",
							boxSizing: "border-box",
							wordBreak: "break-word !important",
							overflowWrap: "anywhere !important",
							whiteSpace: "normal !important"
						}
					}}
				>
					<div
						style={{
							wordBreak: "break-word",
							overflowWrap: "anywhere",
							overflow: "hidden",
							whiteSpace: "normal"
						}}
						dangerouslySetInnerHTML={{ __html: htmlText }}
					/>
				</MDBox>
			) : (
				<MDBox
					sx={{
						fontSize: 12,
						color: labelColor,
						wordBreak: "break-word",
						overflowWrap: "anywhere",
						overflow: "hidden",
						minWidth: 0,
						"& a": { color: isContact ? "#1A73E8" : "#bbdefb", wordBreak: "break-all" },
						"& img": { maxWidth: "100%", height: "auto", display: "block" },
						"& pre": { whiteSpace: "pre-wrap !important", wordBreak: "break-word !important" },
						"& table": { maxWidth: "100%", tableLayout: "fixed" },
						"& td, & th": { wordBreak: "break-word", overflowWrap: "anywhere" },
						"& *": {
							maxWidth: "100% !important",
							boxSizing: "border-box",
							wordBreak: "break-word !important",
							overflowWrap: "anywhere !important",
							whiteSpace: "normal !important"
						}
					}}
				>
					<div
						style={{
							wordBreak: "break-word",
							overflowWrap: "anywhere",
							overflow: "hidden",
							whiteSpace: "normal"
						}}
						dangerouslySetInnerHTML={{ __html: answer }}
					/>
				</MDBox>
			)}
		</MDBox>
	);
};

export default Message;
